import * as R from 'ramda';
import {
  IProductDetailsFilterParams,
  ProductDetailsColor,
  ProductDetailsFilterDeliveryWindow,
} from '../../../../../services/modules/productDetails/productDetails.types';
import { AvailabilityEnum, CompactAvailability } from '../../../../products/ProductTypes';
import { availabilityToCompactAvailability } from '../../../../products/utils/availability.utils';
import { offlineDeliveryWindowsRepository } from '../../../repositories/offlineDeliveryWindows.repository';
import { offlineProductColorsRepository } from '../../../repositories/offlineProductColors.repository';
import OfflineProductColorToProductDetailsTransformers from '../../../transformers/productColor/OfflineDatabaseProductColor.toProductDetails.transformers';

export default class ProductDetailsFilterUtils {
  static async getAvailabilitiesFilter(
    params: IProductDetailsFilterParams,
  ): Promise<CompactAvailability[]> {
    if (params.productColorId) {
      return this.getAvailabilitiesFromProductColor(params.productColorId);
    }
    if (!params.productId) {
      throw new Error('product id not provided to filter availabilities');
    }
    return this.getAllAvailabilitiesFromProduct(params.productId);
  }

  private static async getAvailabilitiesFromProductColor(
    productColorId: string,
  ): Promise<CompactAvailability[]> {
    const productColor = await offlineProductColorsRepository.getByIdOrFail(productColorId);
    return this.toCorrectedAvailabilities(productColor.availabilities);
  }

  private static async getAllAvailabilitiesFromProduct(
    productId: string,
  ): Promise<CompactAvailability[]> {
    const allProductColors = await offlineProductColorsRepository.findByProductId(productId);
    const availabilitiesSet = new Set<AvailabilityEnum>();
    allProductColors.forEach((color) => {
      color.availabilities.forEach((availability) => availabilitiesSet.add(availability));
    });
    return this.toCorrectedAvailabilities(Array.from(availabilitiesSet));
  }

  private static toCorrectedAvailabilities(
    availabilities: AvailabilityEnum[],
  ): CompactAvailability[] {
    const corrected = availabilities
      .map(availabilityToCompactAvailability)
      .filter((availability): availability is CompactAvailability => availability !== undefined);
    const unique = R.uniq(corrected);
    const ordered = unique.sort();
    return ordered;
  }

  static async getColorsFilter(
    params: IProductDetailsFilterParams,
  ): Promise<ProductDetailsColor[]> {
    if (!params.productId) {
      throw new Error('product id not provided to filter colors');
    }
    return this.getAllColorsFromProduct(params.productId);
  }

  private static async getAllColorsFromProduct(productId: string): Promise<ProductDetailsColor[]> {
    const allProductColors = await offlineProductColorsRepository.findByProductId(productId);
    const ordered = R.sortBy((productColor) => productColor.name.toLowerCase(), allProductColors);
    return ordered.map(OfflineProductColorToProductDetailsTransformers.toProductDetailsColor);
  }

  static async getDeliveryWindowFilter(params: IProductDetailsFilterParams) {
    if (!params.productId) {
      throw new Error('product id not provided to filter delivery windows');
    }
    const allProductColors = await offlineProductColorsRepository.findByProductId(params.productId);
    const deliveryWindowIdsSet = new Set<string>();
    allProductColors.forEach((productColor) => {
      productColor.deliveryWindowIds?.preOrder?.forEach((id) => deliveryWindowIdsSet.add(id));
      productColor.deliveryWindowIds?.futureStock?.forEach((id) => deliveryWindowIdsSet.add(id));
    });

    const deliveryWindows = await offlineDeliveryWindowsRepository.findByIds(
      Array.from(deliveryWindowIdsSet),
    );
    const deliveryWindowsMap = new Map(deliveryWindows.map((dw) => [dw.id, dw]));

    const deliveryWindowList: ProductDetailsFilterDeliveryWindow[] = [];
    allProductColors.forEach((productColor) => {
      const deliveryWindowIds = R.uniq([
        ...(productColor.deliveryWindowIds.futureStock ?? []),
        ...(productColor.deliveryWindowIds.preOrder ?? []),
      ]);
      deliveryWindowIds.forEach((id) => {
        const deliveryWindow = deliveryWindowsMap.get(id);

        deliveryWindow &&
          deliveryWindowList.push({
            id: deliveryWindow.id,
            name: deliveryWindow.name,
            productColorId: productColor.id,
          });
      });
    });

    return deliveryWindowList;
  }
}
